import { createContext, useCallback, useEffect, useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';

type SelectionContextType = {
  showSelection: boolean;
  toggleShowSelection: () => void;
  selectedMedialinks: string[];
  selectedSlideSets: number[];
  selectedUserMaterials: string[];
  addMedialinks: (ids: string[]) => void;
  addSlideSets: (ids: number[]) => void;
  addUserMaterials: (ids: string[]) => void;
  removeMedialinks: (ids: string[]) => void;
  removeSlideSets: (ids: number[]) => void;
  removeUserMaterials: (ids: string[]) => void;
  countSelection: () => number;
  clearSelection: () => void;
};

const SelectionContext = createContext<SelectionContextType>({
  showSelection: false,
  toggleShowSelection: () => {},
  selectedMedialinks: [],
  selectedSlideSets: [],
  selectedUserMaterials: [],
  addMedialinks: () => {},
  addSlideSets: () => {},
  addUserMaterials: () => {},
  removeMedialinks: () => {},
  removeSlideSets: () => {},
  removeUserMaterials: () => {},
  countSelection: () => 0,
  clearSelection: () => {},
});

export default SelectionContext;

export function SelectionContextProvider({ children }: PropsWithChildren) {
  const [showSelection, setShowSelection] = useState<boolean>(false);

  const [selectedMedialinks, setSelectedMedialinks] = useState<string[]>([]);
  const [selectedSlideSets, setSelectedSlideSets] = useState<number[]>([]);
  const [selectedUserMaterials, setSelectedUserMaterials] = useState<string[]>([]);

  const location = useLocation();

  const toggleShowSelection = () => {
    if (showSelection) clearSelection();

    setShowSelection(prev => !prev);
  };

  const addMedialinks = (ids: string[]) => {
    setSelectedMedialinks(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
  };

  const addSlideSets = (ids: number[]) => {
    setSelectedSlideSets(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
  };

  const addUserMaterials = (ids: string[]) => {
    setSelectedUserMaterials(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
  };

  const removeMedialinks = (ids: string[]) => {
    setSelectedMedialinks(prev => prev.filter(mid => !ids.includes(mid)));
  };

  const removeSlideSets = (ids: number[]) => {
    setSelectedSlideSets(prev => prev.filter(sid => !ids.includes(sid)));
  };

  const removeUserMaterials = (ids: string[]) => {
    setSelectedUserMaterials(prev => prev.filter(uid => !ids.includes(uid)));
  };

  const clearSelection = () => {
    setSelectedMedialinks([]);
    setSelectedSlideSets([]);
    setSelectedUserMaterials([]);
  };

  const countSelection = useCallback(
    () => selectedMedialinks.length + selectedSlideSets.length + selectedUserMaterials.length,
    [selectedMedialinks, selectedSlideSets, selectedUserMaterials],
  );

  useEffect(() => {
    if (showSelection) toggleShowSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <SelectionContext.Provider
      value={{
        showSelection,
        toggleShowSelection,
        selectedUserMaterials,
        selectedSlideSets,
        selectedMedialinks,
        addUserMaterials,
        addSlideSets,
        addMedialinks,
        removeUserMaterials,
        removeSlideSets,
        removeMedialinks,
        countSelection,
        clearSelection,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
}
